import { EmailStatusCodes } from '@omni/classes/activity/email.activity.class';
import { MedicalInquiryStatus } from '@omni/classes/medical-inquiry/medical-inquiry.class';
import { SetBookingStatus } from '@omni/classes/activity/set-booking.activity.class';
import { TimeOffStatus } from '@omni/classes/activity/timeoff.class';
import { CalendarActivityType } from './calendar-activity-color.model';

/**
 * Status -> config key name, per activity type. This is what lets an admin write
 * `"cancelled"` rather than `"808210005"` - the same word is a different number on every
 * entity. Not configurable: option sets change with the app version, and a customer-edited
 * map would go stale on an offline device with no way to fix it by deploy.
 *
 * No per-type default. An unrecognised status resolves to no key and the caller falls
 * back to `base`, which is also what an uncoloured status does.
 */
export interface StatusMapEntry {
  /** Keyed on `statuscode`. Checked first. */
  readonly byStatus?: Readonly<Record<number, string>>;
  /** Keyed on `statecode`. Checked when no `byStatus` entry matches. */
  readonly byState?: Readonly<Record<number, string>>;
}

/** Appointment statecode: 0 Open, 1 Completed, 2 Cancelled, 3 Scheduled. */
const APPOINTMENT_LIKE: StatusMapEntry = {
  byState: {
    0: 'open',
    1: 'completed',
    2: 'cancelled',
    3: 'scheduled',
  },
};

export const STATUS_MAP: Readonly<Record<CalendarActivityType, StatusMapEntry>> = {
  // Cancelled only reaches the calendar when indskr_displaycancelledmeetingoncalendar
  // is on for the BU, so a `cancelled` colour is invisible until that is enabled.
  [CalendarActivityType.Meeting]: APPOINTMENT_LIKE,
  [CalendarActivityType.StoreCheck]: APPOINTMENT_LIKE,
  [CalendarActivityType.PhoneCall]: APPOINTMENT_LIKE,

  // `incident.statuscode`. byState is kept for records the app creates offline: those
  // carry a placeholder statuscode 0 until they sync, and resolve through `open`.
  [CalendarActivityType.CustomerInquiry]: {
    byStatus: {
      1: 'inProgress',
      2: 'onHold',
      3: 'waitingForDetails',
      4: 'researching',
      5: 'problemSolved',
      1000: 'informationProvided',
      6: 'cancelled',
      2000: 'merged',
    },
    byState: { 0: 'open', 1: 'completed', 2: 'cancelled' },
  },

  // `failed` is its own key rather than sharing one with `sent` - the defect this fixes.
  [CalendarActivityType.Message]: {
    byStatus: {
      [EmailStatusCodes.Read]: 'read',
      [EmailStatusCodes.Draft]: 'draft',
      [EmailStatusCodes.Completed]: 'completed',
      [EmailStatusCodes.Sent]: 'sent',
      [EmailStatusCodes.Received]: 'received',
      [EmailStatusCodes.Canceled]: 'cancelled',
      [EmailStatusCodes.PendingSend]: 'pendingSend',
      [EmailStatusCodes.Sending]: 'sending',
      [EmailStatusCodes.Failed]: 'failed',
      [EmailStatusCodes.Scheduled]: 'scheduled',
      [EmailStatusCodes.Shared]: 'shared',
    },
  },

  // Time off is an approval workflow, not an open/completed one.
  [CalendarActivityType.TimeOff]: {
    byStatus: {
      [TimeOffStatus.Open]: 'open',
      [TimeOffStatus.InReview]: 'inReview',
      [TimeOffStatus.Approved]: 'approved',
      [TimeOffStatus.NotApproved]: 'notApproved',
    },
  },

  // The sync/error statuses get their own keys; today they look like a healthy booking.
  [CalendarActivityType.KitBooking]: {
    byStatus: {
      [SetBookingStatus.DRAFT]: 'draft',
      [SetBookingStatus.REQUESTED]: 'requested',
      [SetBookingStatus.SUBMITTED]: 'submitted',
      [SetBookingStatus.COMPLETED]: 'completed',
      [SetBookingStatus.CANCELLED]: 'cancelled',
      [SetBookingStatus.RETURNREQUESTED]: 'returnRequested',
      [SetBookingStatus.PENDINGSYNC]: 'pendingSync',
      [SetBookingStatus.SYNCERROR]: 'syncError',
      [SetBookingStatus.PENDINGCANCEL]: 'pendingCancel',
      [SetBookingStatus.CANCELERROR]: 'cancelError',
    },
  },

  // `task.statuscode`, the same option set FollowUpActivityDataService fetches live for
  // the detail page's sub-status. Hardcoded here like every other type: a runtime map
  // cannot be corrected on an offline device. A dismissed suggested follow-up (state 2 +
  // status 6) never reaches the calendar, so `cancelled` is rarely seen.
  [CalendarActivityType.FollowUp]: {
    byStatus: {
      2: 'notStarted',
      3: 'inProgress',
      4: 'waitingOnSomeoneElse',
      7: 'deferred',
      5: 'completed',
      6: 'cancelled',
    },
    byState: { 0: 'open', 1: 'completed', 2: 'cancelled' },
  },

  // Labels from `orderStatusString`. statecode 1 has no entry on purpose: "done" for an
  // order is `fulfilled`, which arrives on statuscode.
  [CalendarActivityType.Order]: {
    byStatus: {
      4: 'cancelled',
      548910000: 'forReview',
      548910001: 'approved',
      548910003: 'rejected',
      100001: 'fulfilled',
    },
    byState: { 0: 'open', 2: 'cancelled' },
  },

  // Labels from `surgeryOrderStatusString`; 100001 and 548910001 both mean Completed.
  [CalendarActivityType.ProcedureLog]: {
    byStatus: {
      100001: 'completed',
      548910001: 'completed',
      548910005: 'cancelled',
    },
    byState: { 0: 'open', 1: 'completed', 2: 'cancelled' },
  },

  // Procedure trackers have no dedicated status enum; fall back to state.
  [CalendarActivityType.ProcedureTracker]: {
    byState: { 0: 'open', 1: 'completed', 2: 'cancelled' },
  },

  // Allocation orders. State only: the class exposes no status-string getter, and
  // `statuscode` defaults to 1 for every record.
  [CalendarActivityType.AllocationOrder]: {
    byState: { 0: 'open', 1: 'completed', 2: 'cancelled', 3: 'scheduled' },
  },

  // No `open` key: a medical inquiry starts at Draft, so statecode 0 is `draft`.
  [CalendarActivityType.MedicalInquiry]: {
    byStatus: {
      [MedicalInquiryStatus.DRAFT]: 'draft',
      [MedicalInquiryStatus.INACTIVE]: 'inactive',
      [MedicalInquiryStatus.SUBMITTED]: 'submitted',
      [MedicalInquiryStatus.COMPLETED]: 'completed',
    },
    byState: { 0: 'draft', 1: 'completed', 2: 'inactive' },
  },
};

/**
 * The keys each type accepts in configuration, derived from STATUS_MAP so the two cannot
 * disagree. A key absent here can never match a real activity, so the parser drops it
 * rather than leaving it in memory looking configured.
 */
export const CALENDAR_STATUS_KEYS: Readonly<Record<CalendarActivityType, readonly string[]>> =
  Object.freeze(
    Object.fromEntries(
      (Object.keys(STATUS_MAP) as CalendarActivityType[]).map(type => {
        const entry = STATUS_MAP[type];
        const keys = new Set<string>([
          ...Object.values(entry.byStatus ?? {}),
          ...Object.values(entry.byState ?? {}),
        ]);
        return [type, Object.freeze([...keys])];
      }),
    ),
  ) as Readonly<Record<CalendarActivityType, readonly string[]>>;

/** Resolve raw state/status to a config key name, or null if unrecognised. Null is not an
 *  error - the caller uses `base` and reports it once per session so drift surfaces. */
export function resolveStatusKey(
  configKey: CalendarActivityType,
  state: number | undefined,
  status: number | undefined,
): string | null {
  const entry = STATUS_MAP[configKey];
  if (!entry) return null;

  if (status !== undefined && status !== null && entry.byStatus) {
    const byStatus = entry.byStatus[status];
    if (byStatus) return byStatus;
  }

  if (state !== undefined && state !== null && entry.byState) {
    const byState = entry.byState[state];
    if (byState) return byState;
  }

  return null;
}
